import { useState, useEffect } from 'react';
import { DollarSign, AlertTriangle, Wallet, TrendingUp, ArrowRight, ExternalLink, Download, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PageLoader } from '@shared/components/PageLoader';
import { billingAPI } from '../api';
import { BillingFlow } from './BillingFlow';
import type { BillingOverview, BillingInvoice, BillingAccount, BillingSettings } from '../types';

function money(n: number | null | undefined): string {
  return (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function daysOverdue(due: string | null): number {
  if (!due) return 0;
  const t = new Date(due).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.floor((Date.now() - t) / 86_400_000);
}

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  paid: 'default',
  open: 'secondary',
  draft: 'outline',
  uncollectible: 'destructive',
  void: 'outline',
};

export function BillingPage() {
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [accounts, setAccounts] = useState<BillingAccount[]>([]);
  const [settings, setSettings] = useState<BillingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [ov, inv, acc, set] = await Promise.all([
          billingAPI.getOverview(),
          billingAPI.getInvoices(),
          billingAPI.getAccounts(),
          billingAPI.getSettings(),
        ]);
        setOverview(ov);
        setInvoices(inv.invoices);
        setAccounts(acc.accounts);
        setSettings(set.settings);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load billing data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isLive = !!settings?.billing_live && !settings?.kill_switch;

  if (loading) return <PageLoader />;
  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      </div>
    );
  }

  const aging = overview?.aging;
  // Nothing left to collect is not an open invoice — a zero-amount row is a superseded
  // or bad invoice and only adds noise. If a balance is ever restored it reappears here.
  const openInvoices = invoices.filter(
    (i) => (i.status === 'open' || i.status === 'uncollectible') && Number(i.amount_remaining) > 0,
  );

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <DollarSign className="h-6 w-6" /> Billing &amp; Cash
        </h1>
        <p className="text-muted-foreground text-sm">
          Accounts receivable, invoice status, cash position, and recommended money moves.
        </p>
      </div>

      <Tabs defaultValue="dashboard" className="mt-2">
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="flow">System Flow</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6 mt-4">

      {settings && (
        <div className={`rounded-lg border p-3 flex flex-wrap items-center gap-2 text-sm ${isLive ? 'border-success/30 bg-success/10 text-success' : 'border-warning/30 bg-warning/10 text-warning'}`}>
          {isLive ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
          <span className="font-semibold">{isLive ? 'LIVE' : 'SAFE MODE'}</span>
          <span>{isLive ? 'Invoices auto-send to clients on their billing day.' : 'No client emails will send — invoices are created as drafts only.'}</span>
          <span className="ml-auto text-xs text-muted-foreground">
            Tax reserve {settings.tax_reserve_pct}% · Min balance ${Number(settings.min_operating_balance).toLocaleString()}
          </span>
        </div>
      )}

      {/* KPI row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={<TrendingUp className="h-4 w-4" />} label="MRR (active)" value={money(overview?.mrr)} />
        <Kpi icon={<AlertTriangle className="h-4 w-4" />} label="Total AR (open)" value={money(aging?.total)}
             accent={aging && aging.total > 0 ? 'amber' : undefined} />
        <Kpi icon={<AlertTriangle className="h-4 w-4" />} label="Overdue 60+"
             value={money((aging?.['61-90'] || 0) + (aging?.['90+'] || 0))}
             accent={aging && (aging['61-90'] + aging['90+']) > 0 ? 'red' : undefined} />
        <Kpi icon={<Wallet className="h-4 w-4" />} label="Cash position" value={money(overview?.cashTotal)} />
      </div>

      {/* AR aging + Recommendations */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">AR Aging</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {aging && ([
              ['Current', aging.current],
              ['1–30 days', aging['0-30']],
              ['31–60 days', aging['31-60']],
              ['61–90 days', aging['61-90']],
              ['90+ days', aging['90+']],
            ] as [string, number][]).map(([label, val]) => (
              <div key={label} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className={val > 0 && label.includes('9') ? 'font-medium text-destructive' : 'font-medium'}>
                  {money(val)}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Recommended money moves</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {(overview?.recommendations.length ?? 0) === 0 && (
              <p className="text-sm text-muted-foreground">No moves recommended right now.</p>
            )}
            {overview?.recommendations.map((m) => (
              <div key={m.id} className="text-sm border rounded-md p-3">
                <div className="flex items-center justify-between font-medium">
                  <span>{money(m.amount)}</span>
                  <span className="text-muted-foreground flex items-center gap-1 text-xs">
                    {m.from_label} <ArrowRight className="h-3 w-3" /> {m.to_label}
                  </span>
                </div>
                {m.reason && <p className="text-muted-foreground text-xs mt-1">{m.reason}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Cash accounts */}
      {overview && overview.cash.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Cash by account</CardTitle></CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {overview.cash.map((c) => (
                <div key={`${c.source}:${c.account_label}`} className="border rounded-md p-3">
                  <div className="text-xs text-muted-foreground uppercase">{c.account_label}</div>
                  <div className="text-lg font-semibold">{money((c.available || 0) + (c.pending || 0))}</div>
                  {!!c.pending && <div className="text-xs text-muted-foreground">{money(c.pending)} pending</div>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Open invoices */}
      <Card>
        <CardHeader><CardTitle className="text-base">Open invoices ({openInvoices.length})</CardTitle></CardHeader>
        <CardContent>
          {/* Mobile: one card per invoice — a 6-column table cannot be read on a phone */}
          <ul className="grid gap-3 sm:hidden">
            {openInvoices.map((inv) => {
              const od = daysOverdue(inv.due_date);
              return (
                <li key={inv.id} className="border rounded-md p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-mono text-xs break-words">{inv.number || inv.stripe_invoice_id}</div>
                      <div className="text-sm text-muted-foreground mt-0.5">{inv.client?.company_name ?? '—'}</div>
                    </div>
                    <div className="font-semibold tabular-nums shrink-0">{money(inv.amount_remaining)}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    <Badge variant={STATUS_VARIANT[inv.status] ?? 'secondary'}>{inv.status}</Badge>
                    <span className={od > 0 ? 'text-destructive text-xs' : 'text-xs text-muted-foreground'}>
                      {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : 'No due date'}
                      {od > 0 && ` · ${od}d overdue`}
                    </span>
                    {inv.hosted_invoice_url && (
                      <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer"
                         aria-label={`Open invoice ${inv.number ?? ''} in a new tab`}
                         className="ml-auto text-muted-foreground hover:text-foreground">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
            {openInvoices.length === 0 && (
              <li className="text-center text-muted-foreground py-6">No open invoices 🎉</li>
            )}
          </ul>

          <div className="hidden sm:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="text-right">Amount due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Due</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {openInvoices.map((inv) => {
                const od = daysOverdue(inv.due_date);
                return (
                  <TableRow key={inv.id}>
                    <TableCell className="font-mono text-xs">{inv.number || inv.stripe_invoice_id}</TableCell>
                    <TableCell>{inv.client?.company_name ?? '—'}</TableCell>
                    <TableCell className="text-right">{money(inv.amount_remaining)}</TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[inv.status] ?? 'secondary'}>{inv.status}</Badge></TableCell>
                    <TableCell className={od > 0 ? 'text-destructive text-sm' : 'text-sm'}>
                      {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}
                      {od > 0 && <span className="ml-1 text-xs">({od}d overdue)</span>}
                    </TableCell>
                    <TableCell>
                      {inv.hosted_invoice_url && (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer"
                           className="text-muted-foreground hover:text-foreground">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {openInvoices.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No open invoices 🎉</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Client billing config + export */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Client billing config</CardTitle>
          <Button variant="outline" size="sm" onClick={() => billingAPI.downloadInvoicesCsv().catch((e) => setError(String(e)))}>
            <Download className="h-4 w-4 mr-1" /> Export CSV (QuickBooks)
          </Button>
        </CardHeader>
        <CardContent>
          {/* Mobile: one card per client */}
          <ul className="grid gap-3 sm:hidden">
            {accounts.map((a) => (
              <li key={a.id} className="border rounded-md p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 font-medium break-words">{a.client?.company_name ?? '—'}</div>
                  <div className="font-semibold tabular-nums shrink-0">{money(a.retainer_amount)}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-1">Bills on day {a.billing_day}</div>
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <Badge variant={a.status === 'active' ? 'secondary' : 'outline'}>{a.status}</Badge>
                  <Badge variant={a.auto_send ? 'default' : 'outline'}>auto-send {a.auto_send ? 'on' : 'off'}</Badge>
                  <Badge variant={a.stripe_customer_id ? 'secondary' : 'destructive'}>
                    {a.stripe_customer_id ? 'linked' : 'unlinked'}
                  </Badge>
                </div>
              </li>
            ))}
            {accounts.length === 0 && (
              <li className="text-center text-muted-foreground py-6">No billing accounts configured.</li>
            )}
          </ul>

          <div className="hidden sm:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead className="text-right">Retainer</TableHead>
                <TableHead>Billing day</TableHead>
                <TableHead>Auto-send</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Stripe</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{a.client?.company_name ?? '—'}</TableCell>
                  <TableCell className="text-right">{money(a.retainer_amount)}</TableCell>
                  <TableCell>Day {a.billing_day}</TableCell>
                  <TableCell>
                    <Badge variant={a.auto_send ? 'default' : 'outline'}>{a.auto_send ? 'on' : 'off'}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={a.status === 'active' ? 'secondary' : 'outline'}>{a.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={a.stripe_customer_id ? 'secondary' : 'destructive'}>
                      {a.stripe_customer_id ? 'linked' : 'unlinked'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {accounts.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No billing accounts configured.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="flow" className="mt-4">
          <BillingFlow overview={overview} accounts={accounts} settings={settings} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Kpi({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: string; accent?: 'amber' | 'red' }) {
  const color = accent === 'red' ? 'text-destructive' : accent === 'amber' ? 'text-warning' : '';
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide">{icon}{label}</div>
        <div className={`text-2xl font-semibold mt-1 ${color}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
